import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Crown, Zap, Star, Clock, Sparkles } from "lucide-react";
import { fetchProgramasLicenciables, fetchMembershipBenefits } from "../../lib/pagos";

const SLUG = "vivienda-multifamiliar";

// Descriptores de acceso por plan (Fase A). NO son precios.
const MEMBRESIA = {
  basico: { label: "Básico", Icon: Star, color: "#64748B", lineas: ["Solo Piso 1", "Funciones limitadas"] },
  pro: { label: "Pro", Icon: Zap, color: "#2563EB", lineas: ["Acceso COREX completo", "Multinivel + exportaciones"] },
  elite: { label: "Premium", Icon: Crown, color: "#B45309", lineas: ["Todo lo de Pro", "Beneficios Premium"] },
};
const ORDEN = ["basico", "pro", "elite"];

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

// Bloque comercial COMPACTO embebido al final del Centro de Diseño (Vivienda Multifamiliar).
export const ProgramaComercial = ({ plan = "basico", licencia = null, isAdmin = false }) => {
  const navigate = useNavigate();
  const [precios, setPrecios] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [selDur, setSelDur] = useState("m6");

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetchProgramasLicenciables().catch(() => []),
      fetchMembershipBenefits().catch(() => ({ planes: [] })),
    ]).then(([lic, ben]) => {
      if (!alive) return;
      const prog = (lic || []).find((p) => p.slug === SLUG);
      setPrecios(prog?.precios || null);
      setBenefits(ben?.planes || []);
    });
    return () => { alive = false; };
  }, []);

  const estado = isAdmin ? "admin" : (licencia?.estado || "no_tiene");
  const opciones = useMemo(() => {
    if (!precios) return [];
    return ["m1", "m3", "m6", "m12"]
      .filter((k) => Number(precios[k]?.centimos) > 0)
      .map((k) => ({
        key: k, meses: k === "m1" ? 1 : k === "m3" ? 3 : k === "m6" ? 6 : 12,
        soles: precios[k]?.soles, ahorro: precios[k]?.ahorro_pct || 0,
      }));
  }, [precios]);

  const verMembresias = () => navigate("/planes");
  const comprar = () => navigate(`/software/licencia/${SLUG}`);
  const precioPlan = (id) => benefits.find((b) => b.id === id)?.precio_label
    || (benefits.find((b) => b.id === id)?.precio === 0 ? "Gratis" : null)
    || MEMBRESIA[id]?.label;

  return (
    <section data-testid="programa-comercial" className="mt-2 rounded-xl border border-slate-200 bg-white shadow-soft overflow-hidden">
      {/* Encabezado compacto + CTA F3 */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
        <span className="text-[11px] font-extrabold uppercase tracking-wide text-slate-700">Acceso &amp; Licencia · COREX Multifamiliar</span>
        {estado === "vigente" && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700" data-testid="pc-licencia-estado">
            <ShieldCheck size={12} /> ACTIVA · vence {fmtFecha(licencia?.fecha_vencimiento)}
          </span>
        )}
        {estado === "vencida" && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700" data-testid="pc-licencia-estado">
            <Clock size={12} /> Licencia vencida
          </span>
        )}
        {estado === "admin" && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500" data-testid="pc-licencia-estado">
            <ShieldCheck size={12} /> Admin · sin licencia requerida
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={verMembresias} data-testid="pc-membresias-btn"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors">
            <Star size={13} className="text-amber-500" /> VER MEMBRESÍAS
          </button>
          {estado === "no_tiene" && (
            <button onClick={comprar} data-testid="pc-comprar-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-indigo-700 transition-colors">
              <Crown size={13} /> COMPRAR LICENCIA
            </button>
          )}
          {estado === "vencida" && (
            <button onClick={comprar} data-testid="pc-renovar-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-amber-700 transition-colors">
              <Clock size={13} /> RENOVAR
            </button>
          )}
        </div>
      </div>

      <div className="p-3">
        {/* Línea discreta Membresía ≠ Licencia */}
        <p className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <Sparkles size={11} className="text-indigo-500" /> La <b className="text-slate-600">Membresía</b> ConstruLink es distinta de la <b className="text-slate-600">Licencia del Programa</b> (activación completa; sin descuentos de membresía).
        </p>

        {/* (A) Membresía — 3 cards horizontales compactas */}
        <div className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-400">A · Acceso por membresía</div>
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          {ORDEN.map((id) => {
            const m = MEMBRESIA[id];
            const actual = plan === id;
            return (
              <div key={id} data-testid={`pc-membresia-${id}`}
                className="relative rounded-lg border px-2.5 py-2"
                style={actual ? { borderColor: m.color, background: `${m.color}0d` } : { borderColor: "#e2e8f0" }}>
                {actual && <span className="absolute -top-1.5 right-2 rounded-full px-1.5 py-px text-[8px] font-extrabold text-white" style={{ background: m.color }}>TU PLAN</span>}
                <div className="flex items-center gap-1.5">
                  <m.Icon size={13} style={{ color: m.color }} />
                  <span className="text-[12px] font-extrabold text-slate-800">{m.label}</span>
                  <span className="ml-auto text-[11px] font-bold text-slate-900">{precioPlan(id)}</span>
                </div>
                <ul className="mt-1 space-y-0.5">
                  {m.lineas.map((l) => (
                    <li key={l} className="text-[10px] leading-tight text-slate-500">· {l}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Separador visual A / B */}
        <div className="my-3 flex items-center gap-2">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">Licencia del programa</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {/* (B) Licencia — 3 opciones compactas */}
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">B · Activación completa COREX</div>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
          {opciones.map((o) => {
            const active = selDur === o.key;
            return (
              <button key={o.key} onClick={() => setSelDur(o.key)} data-testid={`pc-lic-opt-${o.key}`}
                className="relative rounded-lg border px-2.5 py-2 text-left transition-all"
                style={active ? { borderColor: "#4f46e5", background: "#eef2ff" } : { borderColor: "#e2e8f0", background: "#fff" }}>
                {o.ahorro > 0 && (
                  <span className="absolute -top-1.5 right-2 rounded-full bg-emerald-500 px-1.5 py-px text-[8px] font-extrabold text-white" data-testid={`pc-lic-ahorro-${o.key}`}>
                    -{o.ahorro}%
                  </span>
                )}
                <div className="flex items-center gap-1 text-[10px] font-bold text-slate-500">
                  <Clock size={11} className={active ? "text-indigo-600" : "text-slate-400"} /> {o.meses === 1 ? "1 mes" : `${o.meses} meses`}
                </div>
                <div className="mt-0.5 text-[15px] font-extrabold text-slate-900" data-testid={`pc-lic-precio-${o.key}`}>S/ {o.soles}</div>
              </button>
            );
          })}
        </div>
        {(estado === "no_tiene" || estado === "vencida") && (
          <button onClick={comprar} data-testid="pc-lic-comprar-btn"
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-[11px] font-bold text-white hover:bg-indigo-700 transition-colors">
            <Crown size={13} /> {estado === "vencida" ? "RENOVAR LICENCIA" : "COMPRAR LICENCIA"}
          </button>
        )}
      </div>
    </section>
  );
};
